/**
 * Obergrenze fuer den Angebots-Anhang, gespiegelt aus der Server-Konfiguration
 * (`profiler.file-storage.categories.offer-attachments.max-size: 10MB`, von Spring
 * als 10 MiB gelesen). Nur zum fruehen Abfangen — durchgesetzt wird sie serverseitig.
 */
export const OFFER_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * Dateiname aus dem `Content-Disposition`-Header.
 *
 * Der Server schickt bei Namen ausserhalb von ASCII beide Formen: `filename` mit
 * ersetzten Zeichen und zusaetzlich `filename*` nach RFC 5987. Die Sternform gewinnt,
 * weil nur sie den Originalnamen traegt.
 */
export function fileNameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const extended = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (extended) {
    const clean = sanitizeFileName(decodeSafely(extended[1].trim()));
    if (clean) return clean;
  }

  const quoted = /filename\s*=\s*"([^"]*)"/.exec(header);
  const plain = quoted ?? /filename\s*=\s*([^;]+)/.exec(header);
  return plain ? sanitizeFileName(plain[1].trim()) : null;
}

/**
 * Verzeichnistrenner und Steuerzeichen raus, bevor der Name in `a.download` landet:
 * er stammt aus einer hochgeladenen Datei, also aus Benutzereingabe.
 */
export function sanitizeFileName(value: string | null | undefined): string | null {
  if (!value) return null;
  const withoutPath = value.replace(/[\\/]/g, '_');
  // eslint-disable-next-line no-control-regex
  const printable = withoutPath.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  const withoutDots = printable.replace(/^\.+/, '');
  return withoutDots.length > 0 ? withoutDots : null;
}

/**
 * `null` bei ungueltiger Prozent-Kodierung — ein abgeschnittenes oder umgeschriebenes
 * `filename*` faellt damit auf die mitgesendete ASCII-Form durch. Der rohe Prozent-String
 * waere ein gueltiger Dateiname und haette den Fallback unerreichbar gemacht.
 */
function decodeSafely(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

/**
 * Blob als Datei speichern: Object-URL, verstecktes `<a>`, Klick, Widerruf. Derselbe
 * Ablauf wird an mehreren Stellen gebraucht und steht deshalb zentral hier.
 */
export function saveBlob(body: Blob, fileName: string): void {
  const url = URL.createObjectURL(body);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Firefox und Safari brechen den Download gelegentlich ab, wenn die URL noch im
  // selben Tick widerrufen wird.
  setTimeout(() => URL.revokeObjectURL(url));
}

/**
 * Dateigroesse fuer die Anzeige: B, KB, MB mit einer Nachkommastelle ab KB. Das
 * Dezimaltrennzeichen folgt der Oberflaechensprache — fest verdrahtet stand in der
 * englischen Ansicht "1,5 MB".
 */
export function formatFileSize(bytes: number | null | undefined, locale?: string): string {
  if (bytes == null || bytes < 0 || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  // Erst runden, dann die Einheit waehlen: 1048570 Byte sind gerundet 1024,0 KB, und
  // "1.024 KB" liest sich auf Deutsch wie 1024 KB, gemeint ist 1 MB.
  const kb = roundToTenth(bytes / 1024);
  if (kb < 1024) return `${format(kb, locale)} KB`;
  return `${format(roundToTenth(bytes / (1024 * 1024)), locale)} MB`;
}

function roundToTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

function format(value: number, locale?: string): string {
  return value.toLocaleString(locale || 'de', {maximumFractionDigits: 1});
}

export type FileKind = 'pdf' | 'doc' | 'sheet' | 'slides' | 'image' | 'archive' | 'text' | 'mail' | 'file';

const KIND_BY_EXTENSION: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'doc', docx: 'doc', odt: 'doc', rtf: 'doc',
  xls: 'sheet', xlsx: 'sheet', xlsm: 'sheet', ods: 'sheet', csv: 'sheet',
  ppt: 'slides', pptx: 'slides', odp: 'slides',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', bmp: 'image', webp: 'image', svg: 'image', heic: 'image',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive',
  txt: 'text', md: 'text', log: 'text',
  msg: 'mail', eml: 'mail',
};

const ICON_BY_KIND: Record<FileKind, string> = {
  pdf: 'picture_as_pdf',
  doc: 'description',
  sheet: 'table_chart',
  slides: 'slideshow',
  image: 'image',
  archive: 'archive',
  text: 'article',
  mail: 'mail',
  file: 'insert_drive_file',
};

export function fileKind(fileName: string | null | undefined): FileKind {
  const extension = /\.([a-z0-9]+)$/i.exec(fileName?.trim() ?? '')?.[1]?.toLowerCase();
  // `hasOwn`, sonst liefert eine Datei namens `x.constructor` das geerbte Object-Member.
  return extension && Object.hasOwn(KIND_BY_EXTENSION, extension) ? KIND_BY_EXTENSION[extension] : 'file';
}

export function fileIcon(fileName: string | null | undefined): string {
  return ICON_BY_KIND[fileKind(fileName)];
}
